// Item 428 finding 5: "Generate key" for virtual Debian/RPM/Alpine repos
// that were created before keys were minted automatically. Shown only for
// those formats; hidden when the API refuses (non-admin) or still loading.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { createTrustKey, useTrustKeys, type TrustAlgorithm } from '../api/trust';

// Mirrors api/internal/handlers/repos_virtual_signing.go virtualSigningSpec.
const SPEC: Record<string, { purpose: string; algorithm: TrustAlgorithm }> = {
  debian: { purpose: 'debian_release', algorithm: 'openpgp-rsa-4096' },
  rpm: { purpose: 'rpm_repomd', algorithm: 'openpgp-rsa-4096' },
  alpine: { purpose: 'alpine_apkindex', algorithm: 'rsa-4096' },
};

// Item 423 finding 7: the client-side command that trusts this repo's key.
export function clientTrustCommand(format: string, host: string, project: string, repo: string): string {
  const base = `https://${host}/${format}/${project}/${repo}`;
  switch (format) {
    case 'debian':
      return `curl -fsSL ${base}/key.asc | sudo gpg --dearmor -o /etc/apt/keyrings/${repo}.gpg\n` +
        `echo "deb [signed-by=/etc/apt/keyrings/${repo}.gpg] ${base} stable main" | sudo tee /etc/apt/sources.list.d/${repo}.list`;
    case 'rpm':
      return `sudo rpm --import ${base}/repodata/repomd.xml.key\n# in the .repo file: repo_gpgcheck=1`;
    case 'alpine':
      return `sudo wget -P /etc/apk/keys ${base}/keys/index.rsa.pub`;
    default:
      return '';
  }
}

export function RepoSigningKeyCard({
  repoID,
  format,
  projectSlug,
  repoSlug,
}: {
  readonly repoID: string;
  readonly format: string;
  readonly projectSlug?: string;
  readonly repoSlug?: string;
}) {
  const { t } = useTranslation();
  const { data, error, mutate } = useTrustKeys();
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const spec = SPEC[format];

  if (!spec || error || data === undefined) return null;
  const key = data.find((k) => k.repo_id === repoID && k.purpose === spec.purpose);

  const generate = async () => {
    setErr('');
    setBusy(true);
    try {
      await createTrustKey({ purpose: spec.purpose, algorithm: spec.algorithm, repo_id: repoID });
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader title={t('repoDetail.signingKey.title')} description={t('repoDetail.signingKey.description')} />
      <CardBody>
        {err && <p className="text-sm text-danger mb-3">{err}</p>}
        {key ? (
          <>
            <p className="text-sm">{t('repoDetail.signingKey.present', { fingerprint: key.fingerprint })}</p>
            <p className="text-sm mt-3 mb-1">{t('repoDetail.signingKey.trustHint')}</p>
            <pre className="text-xs bg-surface-muted rounded p-2 overflow-x-auto">
              {clientTrustCommand(
                format,
                globalThis.location?.host ?? 'orbitalreg.example.com',
                projectSlug ?? '<project>',
                repoSlug ?? '<repo>',
              )}
            </pre>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <p className="text-sm">{t('repoDetail.signingKey.missing')}</p>
            <Button disabled={busy} onClick={generate}>
              {t('repoDetail.signingKey.generate')}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
